import { zeroPad } from '../utils/functions'
import { encode_freq, encode_sfaf_location, encode343, encodeM015, pushUnique } from './sfaf-helpers'
import { IAgencyOverride } from '../../shared/types/api/user.type'
import { IRequestEquipment } from '../types/equipment.type'
import { IRequestFrequencyExclusion } from '../types/exercise.type'
import { IHopset } from '../types/spectrum-manager.type'
import {
  EquipmentsAndStationsResult,
  ISFAFContents,
  ISFAFFrequency,
  SFAFAntennaType,
  SFAFObject,
  SFAFStationClassType,
} from '../types/sfaf.type'

/*
  TODOS: 
    1. It's written that 110 should represent frequency or frequencies on the SFAF documentation , but on SFAF generator logic it can be a single frequency only
    2. It's not clear for me are lines 208 and 209 applied correctly
      1) According to the document, 208 should be the User's Net/Code, but we are applying operating_sub_unit name
      2) For 209 it should be Area AFC/DoD AFC/Other Organizations, but we are applying frequency location_afc property(does that sound right?)
    3. 341 line is missing
    4. 340 line should represent equipment nomenclature, but we are displaying equipment's name, is that right?
    5. Lines 360-361 are missing, and 362-363 are hardcoded

*/

export function applyClassificationAndIdentifiers(
  sfaf_obj: SFAFObject,
  frequency: ISFAFFrequency,
  sfafContents: ISFAFContents[],
  excludedFrequencies: IRequestFrequencyExclusion[],
  agencyOverride?: IAgencyOverride | null,
) {
  const yearSuffix = new Date().getFullYear().toString().slice(-2)

  const content = sfafContents.find((c) => c.id === frequency.request_frequency_request_id)

  sfaf_obj['005'] = `U${content?.classification_name ?? ''}`
  sfaf_obj['102'] =
    frequency.id_override ??
    (agencyOverride?.id_prefix || 'ARMY') + yearSuffix + zeroPad(frequency.request_assigned_frequency_id || 0, 4)

  sfaf_obj['105'] = frequency.request_assigned_frequency_list_serial_number ?? ''

  sfaf_obj['110'] =
    frequency.request_frequency_value ??
    encode_freq(
      frequency.request_frequency_single_start_indicator ?? '',
      frequency.request_frequency_single_start_freq ?? 0,
      true,
      frequency.request_frequency_single_end_indicator ?? '',
      frequency.request_frequency_single_end_freq ?? 0,
    )

  sfaf_obj['111'] = []

  for (const exFreq of excludedFrequencies) {
    if (exFreq.request_frequency_group_id === frequency.request_frequency_group_id) {
      pushUnique(
        sfaf_obj['111'],
        encode_freq(exFreq.start_indicator, exFreq.start_freq, exFreq.is_band, exFreq.end_indicator, exFreq.end_freq),
      )
    }
  }
}

const addRSuffix = (s: string, i: number): string => (i ? `${s}, R${zeroPad(i + 1, 2)}` : s)

export function applyLocations(sfaf_obj: SFAFObject, frequency: ISFAFFrequency) {
  // Transmit (Send) location
  sfaf_obj['300'] = frequency.state_region_type_code
  sfaf_obj['301'] = frequency.request_location_transmitter_location
  sfaf_obj['303'] = encode_sfaf_location(
    frequency.request_location_latitude_deg,
    frequency.request_location_latitude_min,
    frequency.request_location_latitude_sec,
    frequency.request_location_longitude_deg,
    frequency.request_location_longitude_min,
    frequency.request_location_longitude_sec,
  )

  // Receive location(s):
  // Typically these are only set if the location is different from the transmit site.
  // But we don't forbid the transmit site:
  // Sgt. Gaylor from the Army had a use case with a weather ballon traveling from one site to another,
  // transmitting all the way, so he wanted to show receiving at the beginning and the end.
  // After the first 4xx field, the values should end in ", R02", ", R03", etc.
  // The keys should *not* end in "/O2", "/O3", etc (which is handled elsewhere).
  if (frequency.receive_locations.length) {
    sfaf_obj['400'] = []
    sfaf_obj['401'] = []
    sfaf_obj['403'] = []
    // for (const rl of frequency.receive_locations) {
    for (let i = 0; i < frequency.receive_locations.length; i++) {
      const rl = frequency.receive_locations[i]
      sfaf_obj['400'].push(addRSuffix(rl.receive_state_region_type_code || '', i))
      sfaf_obj['401'].push(addRSuffix(rl.receive_request_location_transmitter_location || '', i))
      if (
        rl.receive_request_location_latitude_deg &&
        rl.receive_request_location_latitude_min &&
        rl.receive_request_location_latitude_sec &&
        rl.receive_request_location_longitude_deg &&
        rl.receive_request_location_longitude_min &&
        rl.receive_request_location_longitude_sec
      ) {
        sfaf_obj['403'].push(
          addRSuffix(
            encode_sfaf_location(
              rl.receive_request_location_latitude_deg,
              rl.receive_request_location_latitude_min,
              rl.receive_request_location_latitude_sec,
              rl.receive_request_location_longitude_deg,
              rl.receive_request_location_longitude_min,
              rl.receive_request_location_longitude_sec,
            ),
            i,
          ),
        )
      } else {
        sfaf_obj['403'].push('')
      }
    }
  } else {
    sfaf_obj['400'] = [frequency.state_region_type_code || '']
    sfaf_obj['401'] = [frequency.request_location_transmitter_location || '']
    sfaf_obj['403'] = [sfaf_obj['303'] || '']
  }
}

export function applyFieldSendReceive(
  sfaf_obj: SFAFObject,
  key_suffix: string,
  send_prefix: string,
  receive_prefix: string,
  value: string,
  send_receive: string,
) {
  if (send_receive === 'Send Only') {
    const key = send_prefix + key_suffix

    if (Array.isArray(sfaf_obj[key])) {
      pushUnique(sfaf_obj[key], value)
    } else {
      sfaf_obj[key] = value
    }
  } else if (send_receive === 'Receive Only') {
    const key = receive_prefix + key_suffix
    if (Array.isArray(sfaf_obj[key])) {
      pushUnique(sfaf_obj[key], value)
    } else {
      sfaf_obj[key] = value
    }
  } else {
    const key1 = send_prefix + key_suffix
    const key2 = receive_prefix + key_suffix

    if (Array.isArray(sfaf_obj[key1]) && Array.isArray(sfaf_obj[key2])) {
      pushUnique(sfaf_obj[key1], value)
      pushUnique(sfaf_obj[key2], value)
    } else {
      sfaf_obj[key1] = value
      sfaf_obj[key2] = value
    }
  }
}

export function applyEquipmentAndStations(
  sfaf_obj: SFAFObject,
  frequency: ISFAFFrequency,
  equipments: IRequestEquipment[],
  stationClasses: SFAFStationClassType[],
  antennas: SFAFAntennaType[],
): EquipmentsAndStationsResult {
  const matchingEquipments = equipments.filter(
    (e) => e.request_frequency_group_id === frequency.request_frequency_group_id,
  )

  const result: EquipmentsAndStationsResult = {
    sendAntennaRadius: null,
    receiveAntennaRadius: null,
    antennaRadius406: null,
    antennaRadius306: null,
    senderFixed: true,
    receiverFixed: true,
    flightLevel: 0,
  }

  sfaf_obj['113'] = []
  sfaf_obj['114'] = []
  sfaf_obj['115'] = []
  sfaf_obj['340'] = []
  sfaf_obj['343'] = []
  sfaf_obj['440'] = []
  sfaf_obj['443'] = []
  sfaf_obj['501'] = []

  let elevation_antenna_base = ''
  let antenna_feed_point_height = ''

  for (const eq of matchingEquipments) {
    const encodedM015 = encodeM015(eq.f_501_irac, eq.f_501_sps)

    const isSend = eq.send_receive === 'Send Only'
    const isReceive = eq.send_receive === 'Receive Only'
    const isBoth = !isSend && !isReceive // Assume both if not explicitly send/receive only

    // Push equipment names to correct fields
    if (isSend || isBoth) {
      pushUnique(sfaf_obj['340'], eq.equipment_name)
      pushUnique(sfaf_obj['343'], encode343(eq.f_343))
      if (encodedM015) pushUnique(sfaf_obj['501'], encodedM015)
    }
    if (isReceive || isBoth) {
      pushUnique(sfaf_obj['440'], eq.equipment_name)
      pushUnique(sfaf_obj['443'], encode343(eq.f_343))
    }

    for (const sc of stationClasses) {
      if (sc.request_equipment_id !== eq.id) continue

      // Push to 113/114/115
      sfaf_obj['113'].push(sc['113_output'] || '')

      if (frequency.f_114) {
        sfaf_obj['114'].push(frequency.f_114)
      } else if (sc.emission_designator_name) {
        sfaf_obj['114'].push(sc.emission_designator_name)
      } else if (sc.emission_designator_simple_type_name) {
        sfaf_obj['114'].push(`**${sc.emission_designator_simple_type_name}**`)
      } else {
        sfaf_obj['114'].push('')
      }

      pushUnique(sfaf_obj['115'], sc.power_units + '' + sc.power)

      // Determine radius overrides
      const isMobile = ['Aeronautical', 'Maritime', 'Mobile', 'Experimental'].includes(sc.station || '')
      if (isMobile) {
        if (eq.send_receive === 'Send Only') {
          result.sendAntennaRadius = Math.max(result.sendAntennaRadius || 0, sc.antenna_radius)
        } else if (eq.send_receive === 'Receive Only') {
          result.receiveAntennaRadius = Math.max(result.receiveAntennaRadius || 0, sc.antenna_radius)
        } else {
          result.sendAntennaRadius = Math.max(result.sendAntennaRadius || 0, sc.antenna_radius)
          result.receiveAntennaRadius = Math.max(result.receiveAntennaRadius || 0, sc.antenna_radius)
        }
      }

      // Track flight level
      if (sc.station === 'Aeronautical') {
        result.flightLevel = Math.max(result.flightLevel, sc.flight_level)
      }

      // Special logic for Fixed stations
      if (sc.station === 'Fixed') {
        const elevationAntennaBase = `${frequency.request_location_elevation}`
        const feedPointHeight = `${sc.antenna_feed_point_height}`

        if (
          frequency.request_location_radius !== 0 &&
          !frequency.receive_locations.length &&
          eq.send_receive === 'Send Only'
        ) {
          result.antennaRadius406 = frequency.request_location_radius
        } else if (eq.send_receive === 'Receive Only') {
          result.antennaRadius306 = frequency.request_location_radius + 'T'
        }

        // Save elevation + height for antenna loop
        elevation_antenna_base = elevationAntennaBase
        antenna_feed_point_height = feedPointHeight
      } else {
        // Mark as non-fixed
        if (eq.send_receive === 'Send Only') result.senderFixed = false
        else if (eq.send_receive === 'Receive Only') result.receiverFixed = false
        else {
          result.senderFixed = false
          result.receiverFixed = false
        }
      }
    }

    applyFieldSendReceive(sfaf_obj, '40', '3', '4', eq.equipment_name, eq.send_receive)
    //Note these two are both "3" because "441" should not be in the SFAF according to the client
    applyFieldSendReceive(sfaf_obj, '41', '3', '3', eq.number_of_stations_341, eq.send_receive)

    //antenna
    for (let ea = 0; ea < antennas.length; ea++) {
      if (antennas[ea].request_equipment_id === eq.id) {
        applyFieldSendReceive(sfaf_obj, '54', '3', '4', antennas[ea].code, eq.send_receive)
        //applyFieldSendReceive(sfaf_obj, "55", "3", "4", "", eq.send_receive) //Remove, we don’t have enough data from certification documents to populate this
        //applyFieldSendReceive(sfaf_obj, "56", "3", "4", "", eq.send_receive) //Remove, this is a CENTCOM or EUCOM only field, not necessary until much later versions
        applyFieldSendReceive(sfaf_obj, '57', '3', '4', '0', eq.send_receive)
        applyFieldSendReceive(sfaf_obj, '58', '3', '4', elevation_antenna_base, eq.send_receive) // [Elevation at Antenna Base (meters)] .. only available if station class is Fixed
        applyFieldSendReceive(sfaf_obj, '59', '3', '4', antenna_feed_point_height, eq.send_receive) // [Antenna Feed Point Height (meters)] .. only available if station class is Fixed
        applyFieldSendReceive(sfaf_obj, '60', '3', '4', '', eq.send_receive) //Leave blank/remove
        applyFieldSendReceive(sfaf_obj, '61', '3', '4', '', eq.send_receive) //Leave blank/remove
        applyFieldSendReceive(sfaf_obj, '62', '3', '4', 'ND', eq.send_receive) //Static data: ND
        applyFieldSendReceive(sfaf_obj, '63', '3', '4', 'V', eq.send_receive) //Static data: V
      }
    }
  }

  return result
}

export function applyRadiusOverrideLogic(
  sfaf_obj: any,
  frequency: ISFAFFrequency,
  radiusData: {
    sendAntennaRadius: number | null
    receiveAntennaRadius: number | null
    antennaRadius406: number | null
    antennaRadius306: string | null
    senderFixed: boolean
    receiverFixed: boolean
  },
) {
  const { sendAntennaRadius, receiveAntennaRadius, antennaRadius406, antennaRadius306, senderFixed, receiverFixed } =
    radiusData

  if (senderFixed && !receiverFixed) {
    sfaf_obj['406'] = antennaRadius406
  } else if (receiverFixed && !senderFixed) {
    sfaf_obj['306'] = antennaRadius306
  } else if (senderFixed && receiverFixed) {
    sfaf_obj['306'] = ''
  } else {
    if (sendAntennaRadius !== 0 && !frequency.receive_locations.length) {
      sfaf_obj['306'] = sendAntennaRadius + 'B'
    } else if (sendAntennaRadius !== 0 && frequency.receive_locations.length && receiveAntennaRadius !== 0) {
      const maxRadius = Math.max(sendAntennaRadius || 0, receiveAntennaRadius || 0)
      sfaf_obj['306'] = maxRadius + 'B'
    } else if (sendAntennaRadius !== 0 && frequency.receive_locations.length && receiveAntennaRadius === 0) {
      sfaf_obj['306'] = sendAntennaRadius + 'T'
    }
  }
}

export function applyAntennaOverrides(sfaf_obj: SFAFObject, frequency: ISFAFFrequency) {
  sfaf_obj['354'] = frequency.f_354 ?? ''
  sfaf_obj['454'] = frequency.f_454 ?? ''
  sfaf_obj['357'] = frequency.f_357 ?? ''
  sfaf_obj['457'] = frequency.f_457 ?? ''
  sfaf_obj['362'] = frequency.f_362 ?? ''
  sfaf_obj['462'] = frequency.f_462 ?? ''
  sfaf_obj['363'] = frequency.f_363 ?? ''
  sfaf_obj['463'] = frequency.f_463 ?? ''
}

export function applyAgencyAndAdminFields(
  sfaf_obj: SFAFObject,
  frequency: ISFAFFrequency,
  sfafContent: ISFAFContents,
  agencyOverride?: IAgencyOverride | null,
) {
  sfaf_obj['130'] = agencyOverride?.f_130 ?? '3'
  sfaf_obj['140'] = frequency?.start_date?.format('YYYYMMDD') ?? '20240224'
  sfaf_obj['141'] = frequency?.end_date?.format('YYYYMMDD') ?? '20240217'
  sfaf_obj['144'] = agencyOverride?.f_144 ?? 'U'

  sfaf_obj['200'] = sfafContent.tier_200 ?? ''
  sfaf_obj['201'] = sfafContent.tier_201 ?? ''
  sfaf_obj['202'] = sfafContent.tier_202 ?? ''
  sfaf_obj['204'] = sfafContent.tier_204 ?? ''
  sfaf_obj['205'] = sfafContent.tier_205 ?? ''
  sfaf_obj['206'] = sfafContent.tier_206 ?? ''
  sfaf_obj['207'] = sfafContent.tier_207 ?? ''
  // No tier maps to SFAF field 208 (sub-unit was removed)
  sfaf_obj['209'] = frequency.location_afc ?? ''
  sfaf_obj['500'] = frequency.location_afc_code ?? ''
}

export function applyHopset(sfaf_obj: SFAFObject, frequency: ISFAFFrequency, hopset: IHopset) {
  if (frequency.saturn) {
    sfaf_obj['105'] = hopset.f_105
    sfaf_obj['110'] = hopset.f_110
    sfaf_obj['113'] = hopset.f_113
    sfaf_obj['114'] = hopset.f_114
    sfaf_obj['115'] = hopset.f_115
    sfaf_obj['113/02'] = hopset.f_113_02
    sfaf_obj['114/02'] = hopset.f_114_02
    sfaf_obj['115/02'] = hopset.f_115_02

    sfaf_obj['503'] = frequency.hopset
    sfaf_obj['500'] = 'S189'
    sfaf_obj['500/02'] = 'S362'
    sfaf_obj['500/03'] = frequency.location_afc_code
    sfaf_obj['501'] = 'M015'
    sfaf_obj['501/02'] = 'M018'
    sfaf_obj['502'] = 'FREQS CAN ONLY BE USED IN HOPPING MODE AND CANNOT BE USED FOR SINGLE CHANNEL'
    sfaf_obj['502/02'] = 'COMM. HOPSETS CANNOT BE MIXED.'
    sfaf_obj['503/02'] = 'FLL=000'
  }
}
